import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, X } from 'lucide-react';
import PatternBackground from './PatternBackground';
import {
  Phone, Mail,
  Building, Plane, Utensils, QrCode
} from 'lucide-react';
import { useComplimentaryServices } from '../hooks/useComplimentaryServices';
import DashboardFooter from '../pages/dashboard/DashboardFooter';

// ----- Main Component -----
const ComplimentaryServices = ({ onBack }) => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const handleBack = () => {
    navigate('/dashboard');
  };

  const { data: servicesData, isLoading, error } = useComplimentaryServices(activeCategory === 'All' ? null : activeCategory);

  // Icon mapping from API icon names to Lucide components
  const iconMap = {
    Building,
    Plane,
    Utensils,
    // Add more mappings as needed
  };

  // Transform API data to match component format
  const services = servicesData?.data?.services?.map(service => ({
    ...service,
    icon: service.iconName ? React.cloneElement(iconMap[service.iconName] || Building, {
      className: 'text-green-500',
      size: 24,
    }) : <Building className="text-green-500" size={24} />,
  })) || [];

  const categories = ['All', 'Insurance', 'Restaurant', 'Wellness'];

  const filteredServices = services;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0f1117] text-white font-sans flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1117] text-white font-sans flex items-center justify-center">
        <div className="text-center p-6">
          <p className="text-red-500 text-lg mb-2">Failed to load services</p>
          <p className="text-slate-400 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#0f1117]">
      <PatternBackground>
        <div className="w-full px-8 pb-20 relative z-[2] flex-1">

          {/* Top Navigation - Full Width, Left Aligned */}
          <div className="sticky top-0 z-10 border-b border-white/5 px-4 py-3 flex items-center">
            <button onClick={handleBack} className="p-2 hover:bg-white/10 rounded-full transition" aria-label="Go back">
              <ChevronLeft size={24} />
            </button>
            <h1 className="ml-3 text-lg font-semibold">Complimentary Services</h1>
          </div>

          {/* Services Section */}
          <div className="flex-1 overflow-y-auto">
            {/* Category Filters */}
            <div className="px-6 flex gap-3 overflow-x-auto no-scrollbar mb-6">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-6 py-2 rounded-full text-xs font-medium border transition-all ${
                    activeCategory === cat
                      ? 'bg-[#3E3A52] border-white/20 text-white'
                      : 'bg-transparent border-white/10 text-slate-400 hover:border-white/30'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Services List */}
            <div className="px-6 flex gap-6 flex-wrap justify-start pb-6">
              {filteredServices.map(service => (
                <div
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className="w-[248px] bg-[#2D2B3F] p-6 rounded-xl border border-white/5 hover:border-white/20 transition-all cursor-pointer group flex flex-col"
                >
                  <div className="w-12 h-12 bg-[#3E3A52] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                    {service.icon}
                  </div>
                  <div className="flex flex-col gap-6 mt-4 flex-1">
                    <h3 className="font-bold text-lg leading-tight line-clamp-2">{service.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed line-clamp-3 flex-1">{service.desc}</p>
                    <span className="text-[10px] font-bold text-green-500 uppercase tracking-wider">{service.expiry}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Service Detail Modal (Side Drawer) */}
        {selectedService && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedService(null)} />
            <div className="relative w-full max-w-md bg-[#252331] h-full overflow-y-auto shadow-2xl animate-slide-in">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedService.title}</h2>
                    <p className="text-slate-400 text-sm">{selectedService.details.subtitle}</p>
                  </div>
                  <button onClick={() => setSelectedService(null)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition">
                    <X size={20} />
                  </button>
                </div>

                <section className="mb-8">
                  <h4 className="text-sm font-bold mb-2 uppercase text-slate-500 tracking-widest">Overview</h4>
                  <p className="text-sm text-slate-300 leading-relaxed">{selectedService.details.overview}</p>
                </section>

                <section className="mb-8">
                  <h4 className="text-sm font-bold mb-4 uppercase text-slate-500 tracking-widest">What's Included</h4>
                  <ul className="space-y-3">
                    {selectedService.details.included.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="mb-8">
                  <h4 className="text-sm font-bold mb-2 text-green-500 uppercase tracking-widest">How to Redeem</h4>
                  <p className="text-sm font-medium text-white">{selectedService.details.redeem}</p>
                  {selectedService.details.hasQR && (
                    <div className="mt-4 p-4 bg-white rounded-xl w-32 h-32 mx-auto flex items-center justify-center">
                      <QrCode size={80} className="text-black" />
                    </div>
                  )}
                </section>

                {selectedService.details.contact && (
                  <section className="mb-8 border-t border-white/10 pt-6">
                    <h4 className="text-sm font-bold mb-4 text-slate-400">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm">
                        <Phone size={16} className="text-slate-500" /> {selectedService.details.contact.phone}
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Mail size={16} className="text-slate-500" /> {selectedService.details.contact.email}
                      </div>
                    </div>
                  </section>
                )}

                <div className="flex gap-3 mt-10">
                  <button className="flex-1 bg-green-600 hover:bg-green-700 py-3 rounded-full font-bold transition">VISIT PARTNER</button>
                  <button className="flex-1 border border-white/20 hover:bg-white/5 py-3 rounded-full font-bold transition">CONTACT RO</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </PatternBackground>
      <DashboardFooter />
    </div>
  );
};

export default ComplimentaryServices;